import React, { createContext, ReactNode, useCallback, useContext, useEffect } from 'react'
import { authenticate, AuthStatus, selectAuthStatus, SESSION_HINT_KEY, unauthenticate } from '../redux-ts';
import { useAppDispatch, useAppSelector } from '../redux-ts/hooks';
import { useLoginService } from '../services/login.service';

interface AuthProviderProps {
    children: ReactNode
}

interface providerValue {
    isAuthenticated: () => boolean,
    isChecking: () => boolean,
    onLogin: (code: string) => Promise<void>,
    onPromoLogin: (promoCode: string) => Promise<void>
}

const defaultProviderValue : providerValue = {
        isAuthenticated: () => false,
        isChecking: () => false,
        onLogin: async (_:string) => {},
        onPromoLogin: async (_:string) => {}
}

export const AuthContext = createContext<providerValue>(defaultProviderValue);

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({ children }) => {

    const dispatch = useAppDispatch();
    const status = useAppSelector(selectAuthStatus);
    const service = useLoginService();

    const loadSession = async () => {
        try {
            const { data } = await service.me();
            dispatch(authenticate(data.login));
        }
        catch {
            dispatch(unauthenticate());
        }
    }

    // The session cookie is HttpOnly, so ask the API whether it is still valid
    useEffect(() => { loadSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        try {
            if (status === AuthStatus.Authenticated) localStorage.setItem(SESSION_HINT_KEY, '1');
            else if (status === AuthStatus.Anonymous) localStorage.removeItem(SESSION_HINT_KEY);
        }
        catch { }
    }, [status]);

    // Stable reference, so the login page doesn't restart its hub connection when the auth status changes
    const onLogin = useCallback(async (code: string) => {
        await service.createSession({ code });

        const { data } = await service.me();
        dispatch(authenticate(data.login));
    }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const onPromoLogin = useCallback(async (promoCode: string) => {
        await service.promoLogin({ code: promoCode });

        const { data } = await service.me();
        dispatch(authenticate(data.login));
    }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const providerValues : providerValue = {
        isAuthenticated: () => status === AuthStatus.Authenticated,
        isChecking: () => status === AuthStatus.Checking,
        onLogin,
        onPromoLogin
    }

    return (
        <AuthContext.Provider value={providerValues}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
